/**
 * Currency formatting utilities.
 *
 * Money is **always stored in 2-decimal minor units** (i.e. `100 * major`)
 * even for ISO 4217 zero-fraction currencies like TWD/JPY. This keeps
 * arithmetic uniform across the app. The display layer (`formatAmount`)
 * then renders the major value with the currency-correct number of
 * fraction digits, looked up via `Intl.NumberFormat.resolvedOptions()`.
 *
 * Symbols are looked up via `Intl.NumberFormat` with a small override map
 * for codes whose zh-TW narrow form is ambiguous (TWD, USD). Garbage input
 * is tolerated: an unrecognised or malformed currency code falls back to a
 * 3-letter prefix + 2 decimal digits.
 */

const VALID_CURRENCY_CODE = /^[A-Za-z]{3}$/;
const STORAGE_FRACTION_DIGITS = 2;

/**
 * Symbol overrides for currencies where the zh-TW locale's narrow form
 * collides with the local default (e.g. TWD's `$` would be ambiguous next
 * to USD's `$`). The keys here are the canonical compact symbols Mochia
 * displays in zh-TW; everything else falls through to Intl.
 */
const SYMBOL_OVERRIDES: Readonly<Record<string, string>> = {
  TWD: 'NT$',
  USD: '$',
  JPY: '¥',
  EUR: '€',
  HKD: 'HK$',
  CNY: 'CN¥',
  KRW: '₩',
  GBP: '£',
};

/**
 * ISO 4217 fraction-digit overrides for currencies whose Intl/CLDR data
 * disagrees with the standard. Notable: TWD's CLDR `maximumFractionDigits`
 * is 2 in many ICU builds, but ISO 4217 (and Mochia's UX) treats NT$ as a
 * whole-dollar currency. Listing all currencies here is unnecessary — only
 * those that need correction.
 */
const FRACTION_DIGIT_OVERRIDES: Readonly<Record<string, number>> = {
  TWD: 0,
};

interface CurrencyMeta {
  /** Whether the runtime accepted this code as a real ISO 4217 currency. */
  recognised: boolean;
  /** Display fraction digits per ISO 4217 (0 for TWD/JPY, 2 for USD, …). */
  displayFractionDigits: number;
  /** Compact symbol; for fallback this is the 3-letter code. */
  symbol: string;
}

function lookupCurrency(currency: string): CurrencyMeta {
  const code = currency.toUpperCase();
  if (!VALID_CURRENCY_CODE.test(code)) {
    return {
      recognised: false,
      displayFractionDigits: 2,
      symbol: code.slice(0, 3).padEnd(3, '?'),
    };
  }
  try {
    const fmt = new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    });
    const opts = fmt.resolvedOptions();
    const fractionOverride = FRACTION_DIGIT_OVERRIDES[code];
    const displayFractionDigits =
      fractionOverride !== undefined
        ? fractionOverride
        : (opts.maximumFractionDigits ?? 2);
    const overridden = SYMBOL_OVERRIDES[code];
    if (overridden !== undefined) {
      return { recognised: true, displayFractionDigits, symbol: overridden };
    }
    const parts = fmt.formatToParts(0);
    const symbolPart = parts.find((p) => p.type === 'currency');
    const symbol = symbolPart ? symbolPart.value : code;
    return { recognised: true, displayFractionDigits, symbol };
  } catch {
    return {
      recognised: false,
      displayFractionDigits: 2,
      symbol: code,
    };
  }
}

/**
 * Format an amount expressed in **2-decimal minor units** (cents) for display.
 * The final value is rendered with the *currency's* native fraction digits —
 * so e.g. `8550` TWD displays as `NT$ 86` (rounded), `12345` USD as `$ 123.45`.
 *
 * @example formatAmount(8500, 'TWD')   // 'NT$ 85'
 * @example formatAmount(12345, 'USD')  // '$ 123.45'
 * @example formatAmount(12345, 'XYZ')  // 'XYZ 123.45' (fallback)
 */
export function formatAmount(minor: number, currency: string): string {
  const meta = lookupCurrency(currency);
  // Storage is always 2 decimal places — convert to major units first…
  const major = (Number.isFinite(minor) ? minor : 0) / Math.pow(10, STORAGE_FRACTION_DIGITS);
  // …then render with the currency's own fraction digits (Intl will round).
  const numberStr = formatNumberPart(major, meta.displayFractionDigits);
  return `${meta.symbol} ${numberStr}`;
}

function formatNumberPart(major: number, fractionDigits: number): string {
  return new Intl.NumberFormat('zh-TW', {
    style: 'decimal',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(major);
}

// ──────────────────────────────────────────────────────────────────────────
// Monthly totals
// ──────────────────────────────────────────────────────────────────────────

export interface CurrencyTransaction {
  amountMinor: number;
  currency: string;
}

/**
 * Sum a list of transactions filtered by currency, then format the total.
 * Currencies that don't match are ignored — Mochia explicitly avoids any
 * automatic FX conversion, so each currency surface gets its own subtotal.
 */
export function formatAmountMonthlyTotal(
  transactions: readonly CurrencyTransaction[],
  currency: string,
): string {
  const code = currency.toUpperCase();
  let total = 0;
  for (const tx of transactions) {
    if (typeof tx.currency === 'string' && tx.currency.toUpperCase() === code) {
      total += tx.amountMinor;
    }
  }
  return formatAmount(total, currency);
}
